import axios from 'axios';
import { getAPIURLByDomain, getUserData, logoutHandler } from '../utils';
import { toast } from 'react-toastify';
import { isBefore } from 'date-fns';

const sessionExpireHandler = () => {
  toast.error('Your session has expired, please login again', {
    toastId: 'session-expired',
  });
  logoutHandler();
};

const isPasskeyAuthVerifyRequest = (config?: { url?: string }) =>
  config?.url?.includes('passkey_authentication_verify');

const shouldExpireSession = (
  status: number | undefined,
  config?: { url?: string }
) =>
  (status === 401 || status === 403) &&
  !isPasskeyAuthVerifyRequest(config);
const baseUrl = getAPIURLByDomain();

export const instance = axios.create({
  baseURL: baseUrl,
  headers: {
    Accept: 'application/json',
  },
  withCredentials: true
});

instance.interceptors.request.use(
  (config) => {
    config.headers['Session-Token'] = getUserData()?.sessionToken?.value;
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

instance.interceptors.response.use(
  (response) => {
    if (shouldExpireSession(response.status, response.config)) {
      sessionExpireHandler();
    }
    return response;
  },
  (error) => {
    if (shouldExpireSession(error.response?.status, error.config)) {
      sessionExpireHandler();
    }
    return Promise.reject(error);
  }
);

export const criticalInstance = axios.create({
  baseURL: baseUrl,
  headers: {
    Accept: 'application/json',
  },
  withCredentials: true,
});

criticalInstance.interceptors.request.use(
  (config) => {
    const { loginToken } = getUserData();
    const {value: loginTokenValue, expirationDate: loginTokenExpirationDate} = loginToken || {}

    if (loginTokenExpirationDate && isBefore(new Date(loginTokenExpirationDate), new Date())) {
      sessionExpireHandler();
      return Promise.reject(new axios.CanceledError('Session expired'));
    }
    config.headers['Authorization'] = loginTokenValue;
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

criticalInstance.interceptors.response.use(
  (response) => {
    if (response.status === 403) {
      sessionExpireHandler();
    }
    return response;
  },
  (error) => {
    if (
      error.response?.status === 401 ||
      error.response?.status === 403
    ) {
      sessionExpireHandler();
    }
    return Promise.reject(error);
  }
);

export const http = {
  get: (url: string) => instance.get(url).then((response) => response.data),
  post: (url: string, data = {}) =>
    instance.post(url, data).then((response) => response.data),
  put: (url: string, data = {}) =>
    instance.put(url, data).then((response) => response.data),
  patch: (url: string, data = {}) =>
    instance.patch(url, data).then((response) => response.data),
  delete: (url: string) =>
    criticalInstance.delete(url).then((response) => response.data),
  criticalPost: (url: string, data = {}) =>
    criticalInstance.post(url, data).then((response) => response.data),
  criticalGet: (url: string) =>
    criticalInstance.get(url).then((response) => response.data),
  criticalPut: (url: string, data = {}) => 
    criticalInstance.put(url, data).then((response) => response.data),
  criticalDelete: (url: string) =>
    criticalInstance.delete(url).then((response) => response.data),
  criticalPatch: (url: string, data = {}) =>
    criticalInstance.patch(url, data).then((response) => response.data),
};
